import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import {
  ANALYTICS_EVENTS,
  trackAuthFailure,
  trackEvent,
  trackPageView,
} from '../utils/analytics';

const SITE_URL = 'https://ballotr.com';

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView('reset_password');
  }, []);

  const handleReset = async () => {
    setError('');

    if (!email) {
      setError('Please enter your email address.');

      trackEvent(ANALYTICS_EVENTS.PASSWORD_RESET_FAILED, {
        location: 'ResetPassword.tsx',
        reason: 'missing_email',
        email_present: false,
      });

      return;
    }

    setLoading(true);

    trackEvent(ANALYTICS_EVENTS.PASSWORD_RESET_SUBMITTED, {
      location: 'ResetPassword.tsx',
      email_present: Boolean(email),
    });

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) throw error;

      trackEvent(ANALYTICS_EVENTS.PASSWORD_RESET_SUCCEEDED, {
        location: 'ResetPassword.tsx',
        email_present: true,
      });

      toast.success('Password reset email sent!');
    } catch (err: any) {
      const message = err?.message || 'Error sending reset email';
      console.error('Reset password failed:', message);
      setError(message);

      trackAuthFailure({
        flow: 'password_reset',
        location: 'ResetPassword.tsx',
        error: err,
        extraProperties: {
          email_present: Boolean(email),
        },
      });

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Reset Password | Ballotr</title>
        <meta
          name="description"
          content="Request a password reset link for your Ballotr account."
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={`${SITE_URL}/reset-password`} />
      </Helmet>

      <main className="min-h-screen flex items-center justify-center bg-secondary px-4">
        <div className="bg-white shadow-md rounded-lg w-full max-w-md p-6 space-y-6">
          <h2 className="text-lg font-semibold text-textPrimary">Reset Your Password</h2>
          <p className="text-sm text-textSecondary">Enter your email to receive a reset link.</p>

          <input
            type="email"
            placeholder="Email address"
            className="w-full px-4 py-2 border rounded-md"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            autoComplete="email"
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            onClick={handleReset}
            className="w-full bg-primary text-white py-2 rounded-md hover:bg-blue-600 transition disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>

          <button
            onClick={() => navigate('/auth')}
            className="w-full text-sm text-blue-600 hover:underline text-center mt-2"
          >
            Back to Sign In
          </button>
        </div>
      </main>
    </>
  );
};

export default ResetPassword;